import React, { createContext, FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GeneDataGroupType,
  useIsUserFavouriteLazyQuery,
  UserFavouritesResponse,
  useUpdateUserFavouriteMutation,
  useUserFavouritesLazyQuery,
} from "../../../api/smartmelts-api/graphql/generated";
import { useUser } from "../../../components/user/user-context";
import { MenuCategoryDictionary, MenuSubcategoryDictionary } from "../../../models/categories";
import { MenuItemCategory, MenuItemSubcategory } from "../../../models/types/category-types";

interface INutritionContextProviderProps {}

interface INutritionContext {
  activeCategory: MenuItemCategory;
  activeSubcategory: MenuItemSubcategory;
  geneData: GeneDataGroupType | null;
  clearGeneData: () => void;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  changeActiveCategories: (
    category?: MenuItemCategory,
    subcategory?: MenuItemSubcategory,
    index?: number,
    geneData?: GeneDataGroupType,
  ) => void;
  isDemo: boolean;
  isFavourite: boolean;
  refetchIsFavourite: (category?: string, subcategory?: string, index?: number) => void;
  onUpdateUserFavourite: (category?: string, subcategory?: string, index?: number) => void;
  onLoadFavourites: () => void;
  userFavourites: UserFavouritesResponse[];
  favouritesLoading: boolean;
  nextCategory: MenuItemCategory | null;
  nextSubcategory: MenuItemSubcategory | null;
  containerRef: React.RefObject<HTMLDivElement> | null;
  scrollTop: () => void;
}

export const NutritionContext = createContext<INutritionContext>({
  activeCategory: MenuItemCategory.RECOMMENDATIONS,
  activeSubcategory: MenuItemSubcategory.OVERVIEW,
  activeIndex: 1,
  geneData: null,
  clearGeneData: () => {},
  setActiveIndex: (index) => {},
  changeActiveCategories: (
    category?: MenuItemCategory,
    subcategory?: MenuItemSubcategory,
    index?: number,
    geneData?: GeneDataGroupType,
  ) => {},
  isDemo: true,
  isFavourite: false,
  refetchIsFavourite: (category?: string, subcategory?: string, index?: number) => {},
  onUpdateUserFavourite: (category?: string, subcategory?: string, index?: number) => {},
  onLoadFavourites: () => {},
  userFavourites: [],
  favouritesLoading: false,
  nextCategory: null,
  nextSubcategory: null,
  containerRef: null,
  scrollTop: () => {},
});

export const NutritionContextProvider: FunctionComponent<INutritionContextProviderProps> = (props) => {
  const { children } = props;
  const { user, isDemo } = useUser();
  const [activeCategory, setActiveCategory] = useState<MenuItemCategory>(MenuItemCategory.RECOMMENDATIONS);
  const [activeSubcategory, setActiveSubcategory] = useState<MenuItemSubcategory>(MenuItemSubcategory.OVERVIEW);
  const [isFavourite, setIsFavourite] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(1);
  const [geneData, setGeneData] = useState<GeneDataGroupType | null>(null);
  const [favourites, setFavourites] = useState<UserFavouritesResponse[]>([]);
  const [favouritesLoading, setFavouritesLoading] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const changeActiveCategories = (
    category?: MenuItemCategory,
    subcategory?: MenuItemSubcategory,
    index?: number,
    geneData?: GeneDataGroupType,
  ) => {
    if (subcategory !== activeSubcategory) {
      setActiveCategory(category || MenuItemCategory.RECOMMENDATIONS);
      setActiveSubcategory(subcategory || MenuItemSubcategory.OVERVIEW);
      setActiveIndex(index || 1);
      setGeneData(geneData || null);
      isFavouriteQuery({
        variables: {
          category: activeCategory,
          subcategory: activeSubcategory,
          index: index ? index : activeIndex,
        },
      });
    }
  };

  const clearGeneData = useCallback(() => {
    setGeneData(null);
  }, [setGeneData]);

  const refetchIsFavourite = (category?: string, subcategory?: string, index?: number) => {
    isFavouriteQuery({
      variables: {
        category: category ?? activeCategory,
        subcategory: subcategory ?? activeSubcategory,
        index: index ?? activeIndex,
      },
    });
  };

  const [updateUserFavourite] = useUpdateUserFavouriteMutation({
    onCompleted: (data) => {
      userFavouritesQuery();
      if (data.updateUserFavourite) {
        setIsFavourite(true);
      } else {
        setIsFavourite(false);
      }
    },
  });

  const onUpdateUserFavourite = (category?: string, subcategory?: string, index?: number) => {
    setFavouritesLoading(true);
    updateUserFavourite({
      variables: {
        category: category ? category : MenuItemCategory.RECOMMENDATIONS,
        subcategory: subcategory ? subcategory : MenuItemSubcategory.OVERVIEW,
        index: index ? index : activeIndex,
      },
    });
  };

  const [isFavouriteQuery] = useIsUserFavouriteLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data.isUserFavourite) {
        setIsFavourite(true);
      } else {
        setIsFavourite(false);
      }
    },
  });

  const [userFavouritesQuery] = useUserFavouritesLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setFavourites(data.userFavourites);
      setFavouritesLoading(false);
    },
    onError: (_error) => {
      setFavourites([]);
      setFavouritesLoading(false);
    },
  });

  const onLoadFavourites = () => {
    setFavouritesLoading(true);
    userFavouritesQuery();
  };

  useEffect(() => {
    if (!activeSubcategory) {
      setActiveCategory(MenuItemCategory.RECOMMENDATIONS);
      setActiveSubcategory(MenuItemSubcategory.OVERVIEW);
    }
  }, [activeSubcategory]);

  useEffect(() => {
    isFavouriteQuery({
      variables: {
        category: activeCategory,
        subcategory: activeSubcategory,
        index: activeIndex,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubcategory, activeCategory, activeIndex]);

  const categories = Object.entries(MenuCategoryDictionary).filter(([, category]) => !category.hidden);

  const orderedSubcategoryKeys = categories
    .flatMap(([, category]) => category.subcategories)
    .filter((subcategory) => {
      return !(isDemo && !MenuSubcategoryDictionary[subcategory].enabledForDemo);
    });

  const nextSubcategory = useMemo(() => {
    const currentSubcategoryIndex = orderedSubcategoryKeys.indexOf(activeSubcategory);
    if (currentSubcategoryIndex < orderedSubcategoryKeys.length - 1) {
      return orderedSubcategoryKeys[currentSubcategoryIndex + 1];
    }
    return null;
  }, [orderedSubcategoryKeys, activeSubcategory]);

  const nextCategory = useMemo(() => {
    if (nextSubcategory) {
      const [categoryKey] = categories.find(([, category]) => {
        return category.subcategories.includes(nextSubcategory);
      })!;
      return categoryKey ? (categoryKey as MenuItemCategory) : null;
    }
    return null;
  }, [nextSubcategory, categories]);

  const scrollTop = useCallback(() => {
    if (!geneData) {
      setTimeout(() => {
        if (containerRef?.current) {
          containerRef.current.scrollIntoView();
        }
      }, 1);
    }
  }, [containerRef, geneData]);

  return (
    <NutritionContext.Provider
      value={{
        activeSubcategory,
        changeActiveCategories,
        activeIndex,
        setActiveIndex,
        geneData,
        clearGeneData,
        activeCategory,
        isDemo: !!user?.dnaReport?.isDemo,
        isFavourite,
        refetchIsFavourite,
        onUpdateUserFavourite,
        onLoadFavourites,
        userFavourites: favourites,
        favouritesLoading,
        nextCategory,
        nextSubcategory,
        containerRef,
        scrollTop,
      }}
    >
      {children}
    </NutritionContext.Provider>
  );
};
